import { Paperclip, Plus, X } from "lucide-react";
import * as React from "react";
import { useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Button } from "~/components/ui/button";
import {
  type ChatImageAttachment,
  type ChatImageError,
  CHAT_IMAGE_ALLOWED_MIME_TYPES,
  chatImageDataUrl,
  isAllowedChatImageType,
  readChatImageFile,
} from "~/components/ui/chat/imageAttachment";
import {
  type ChatStatus,
  PromptInput,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputToolbar,
  PromptInputTools,
} from "~/components/ui/chat/prompt-input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { useToast } from "~/hooks/use-toast";
import { trackEvent } from "~/utils/analytics";

interface ChatInputProps extends Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  "onSubmit"
> {
  onSend?: (message: string, attachment?: ChatImageAttachment | null) => void;
  isLoading?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({
  className,
  onSend,
  disabled = false,
  isLoading = false,
  ...props
}) => {
  const intl = useIntl();
  const { toast } = useToast();
  const [input, setInput] = useState("");
  const [attachment, setAttachment] = useState<ChatImageAttachment | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const readRequestId = useRef(0);

  const status: ChatStatus = isLoading ? "streaming" : "ready";
  const canSend =
    !disabled && !isLoading && (input.trim().length > 0 || attachment !== null);

  const showImageError = (error: ChatImageError) => {
    const description = intl.formatMessage(
      error === "tooLarge"
        ? {
            id: "chat.attachment.tooLarge",
            defaultMessage: "That image is too big — keep it under 5 MB.",
          }
        : error === "invalidType"
          ? {
              id: "chat.attachment.invalidType",
              defaultMessage:
                "That file type isn't supported — try a PNG, JPEG, WebP, or GIF.",
            }
          : {
              id: "chat.attachment.readFailed",
              defaultMessage: "Couldn't read that image. Please try again.",
            }
    );
    toast({
      variant: "destructive",
      title: intl.formatMessage({
        id: "chat.attachment.errorTitle",
        defaultMessage: "Couldn't attach image",
      }),
      description,
    });
  };

  const handleFiles = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) {
      return;
    }
    const requestId = ++readRequestId.current;
    const result = await readChatImageFile(file);
    if (requestId !== readRequestId.current) {
      return;
    }
    if ("error" in result) {
      showImageError(result.error);
      return;
    }
    trackEvent({ eventName: "app/doc_list/chat_input.attach_image/add" });
    setAttachment(result.attachment);
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const imageItem = Array.from(event.clipboardData.items).find((item) =>
      item.type.startsWith("image/")
    );
    if (!imageItem) {
      return;
    }
    const file = imageItem.getAsFile();
    if (!file) {
      return;
    }
    if (!isAllowedChatImageType(file.type)) {
      return;
    }
    event.preventDefault();
    void handleFiles([file] as unknown as FileList);
  };

  const submit = () => {
    if (!canSend || !onSend) {
      return;
    }
    trackEvent({ eventName: "app/doc_list/send_message.button/submit" });
    onSend(input.trim(), attachment);
    readRequestId.current++;
    setInput("");
    setAttachment(null);
  };

  return (
    <PromptInput
      className={className}
      onSubmit={(_message, event) => {
        event.preventDefault();
        submit();
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={CHAT_IMAGE_ALLOWED_MIME_TYPES.join(",")}
        className="hidden"
        onChange={(e) => {
          void handleFiles(e.target.files);
          e.target.value = "";
        }}
      />
      {attachment && (
        <div className="px-1 pt-1">
          <div className="relative inline-block">
            <img
              src={chatImageDataUrl(attachment.base64, attachment.mimeType)}
              alt={attachment.name}
              className="h-16 w-16 rounded-md border border-border object-cover"
            />
            <Button
              type="button"
              variant="secondary"
              size="icon"
              className="absolute -right-2 -top-2 h-5 w-5 rounded-full border border-border shadow-xs"
              onClick={() => {
                readRequestId.current++;
                setAttachment(null);
              }}
              aria-label={intl.formatMessage({
                id: "chat.attachment.remove",
                defaultMessage: "Remove image",
              })}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}
      <PromptInputTextarea
        value={input}
        disabled={disabled}
        onChange={(e) => setInput(e.target.value)}
        onClick={() => {
          trackEvent({ eventName: "app/doc_list/chat_input.input/click" });
        }}
        onPaste={handlePaste}
        {...props}
      />
      <PromptInputToolbar>
        <PromptInputTools>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={disabled || isLoading}
                aria-label={intl.formatMessage({
                  id: "chat.addFiles",
                  defaultMessage: "Add files",
                })}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="top">
              <DropdownMenuItem onSelect={() => fileInputRef.current?.click()}>
                <Paperclip className="mr-2 h-4 w-4" />
                {intl.formatMessage({
                  id: "chat.attachment.addPhotosFiles",
                  defaultMessage: "Add Photos & Files",
                })}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </PromptInputTools>
        <PromptInputSubmit
          status={status}
          disabled={!canSend}
          aria-label={intl.formatMessage({
            id: "chat.send",
            defaultMessage: "Send Message",
          })}
        />
      </PromptInputToolbar>
    </PromptInput>
  );
};

export { ChatInput };
